import { useAction } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'

import { AsyncStateN } from '@blade/std-lib'
import { openSupportChatAction } from '@root/features/support'
import { ApiError } from '@root/shared/api'
import {
  FastifyErrorCode,
  PremiumErrorCode,
} from '@root/shared/api/api-error-code'
import { GlassProgressDialog } from '@root/shared/ui-kit'

import type { UpdatePremiumCurrencyRequestError } from '@root/features/premium/update-currency'
import type { GlassProgressDialogProps } from '@root/shared/ui-kit'

type Props = {
  previewState: AsyncStateN.AsyncStateN<
    UpdatePremiumCurrencyRequestError,
    unknown
  >
  pollingState: AsyncStateN.AsyncStateN<
    UpdatePremiumCurrencyRequestError,
    unknown
  >
  onReset?: () => void
}

export const UploadEmojiProgress: React.FC<Props> = ({
  previewState,
  pollingState,
  onReset,
}) => {
  const { t } = useTranslation()
  const openSupportChart = useAction(openSupportChatAction)

  const handleSupport = () => {
    openSupportChart()
  }

  if (AsyncStateN.isNotInitiated(previewState)) {
    return
  }

  const params = ((): Partial<GlassProgressDialogProps> | undefined => {
    if (AsyncStateN.isNotInitiated(previewState)) {
      return undefined
    }

    if (AsyncStateN.isError(previewState)) {
      const error = previewState.error
      const message = (() => {
        if (
          ApiError.isOfCodes([
            PremiumErrorCode.ImageContentNotSafe,
            PremiumErrorCode.ImageSafetyCheckFailed,
          ])(error)
        ) {
          return t('premium.update-logo.image-safety-heck-failed')
        }
        if (ApiError.isOfCode(FastifyErrorCode.ContentBodyTooLarge)(error)) {
          return t('common.file-size-too-big')
        }

        return t('common.something_went_wrong_try_again')
      })()

      return {
        title: message,
        children: (
          <>
            <GlassProgressDialog.Button onClick={onReset}>
              {t('premium.update-logo.error.retry')}
            </GlassProgressDialog.Button>
            <GlassProgressDialog.Button onClick={handleSupport}>
              {t('premium.update-logo.error.support')}
            </GlassProgressDialog.Button>
          </>
        ),
      }
    }

    if (AsyncStateN.isError(pollingState)) {
      return {
        title: t('common.something_went_wrong_try_again'),
        children: (
          <>
            <GlassProgressDialog.Button onClick={onReset}>
              {t('premium.update-logo.error.retry')}
            </GlassProgressDialog.Button>
            <GlassProgressDialog.Button onClick={handleSupport}>
              {t('premium.update-logo.error.support')}
            </GlassProgressDialog.Button>
          </>
        ),
      }
    }

    return {
      title: t('premium.image-upload.loading-title'),
      description: t('premium.image-upload.loading-description'),
    }
  })()

  const state = (() => {
    if (!AsyncStateN.isSuccess(previewState)) {
      return previewState
    }

    return pollingState
  })()

  return (
    <GlassProgressDialog
      open={AsyncStateN.isLoading(state) || AsyncStateN.isError(state)}
      title={params?.title ?? ''}
      description={params?.description}
      intent={state}
    >
      {params?.children}
    </GlassProgressDialog>
  )
}
